import classNames from 'classnames';
import debounce from 'lodash.debounce';
import memoize from 'lodash.memoize';
import startsWith from 'lodash.startswith';
import PropTypes from 'prop-types';
import React from 'react';
import './utils/prototypes';

import { ExpandMore as ExpandIcon, Search } from "@mui/icons-material";
import CountryData, { FAKE_SELECT_COUNTRY_ISO } from './CountryData.js';

class PhoneInput extends React.Component {
  static propTypes = {
    country: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    value: PropTypes.string,

    onlyCountries: PropTypes.arrayOf(PropTypes.string),
    preferredCountries: PropTypes.arrayOf(PropTypes.string),
    excludeCountries: PropTypes.arrayOf(PropTypes.string),

    placeholder: PropTypes.string,
    searchPlaceholder: PropTypes.string,
    searchNotFound: PropTypes.string,
    disabled: PropTypes.bool,

    containerStyle: PropTypes.object,
    inputStyle: PropTypes.object,
    buttonStyle: PropTypes.object,
    dropdownStyle: PropTypes.object,
    searchStyle: PropTypes.object,

    containerClass: PropTypes.string,
    inputClass: PropTypes.string,
    buttonClass: PropTypes.string,
    dropdownClass: PropTypes.string,
    searchClass: PropTypes.string,
    // for styled-components
    className: PropTypes.string,

    autoFormat: PropTypes.bool,

    enableAreaCodes: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.arrayOf(PropTypes.string)
    ]),
    enableTerritories: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.arrayOf(PropTypes.string)
    ]),

    disableCountryCode: PropTypes.bool,
    disableDropdown: PropTypes.bool,
    enableLongNumbers: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number
    ]),
    countryCodeEditable: PropTypes.bool,
    enableSearch: PropTypes.bool,
    disableSearchIcon: PropTypes.bool,
    disableInitialCountryGuess: PropTypes.bool,
    disableCountryGuess: PropTypes.bool,

    regions: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string)
    ]),

    inputProps: PropTypes.object,
    localization: PropTypes.object,
    masks: PropTypes.object,
    areaCodes: PropTypes.object,

    preserveOrder: PropTypes.arrayOf(PropTypes.string),

    defaultMask: PropTypes.string,
    alwaysDefaultMask: PropTypes.bool,
    prefix: PropTypes.string,
    copyNumbersOnly: PropTypes.bool,
    renderStringAsFlag: PropTypes.string,
    autocompleteSearch: PropTypes.bool,
    jumpCursorToEnd: PropTypes.bool,
    priority: PropTypes.object,
    enableAreaCodeStretch: PropTypes.bool,
    enableClickOutside: PropTypes.bool,
    showDropdown: PropTypes.bool,

    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    onEnterKeyPress: PropTypes.func,
    onMount: PropTypes.func,
    isValid: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.func,
    ]),
    defaultErrorMessage: PropTypes.string,
    specialLabel: PropTypes.string,
    clearValue: PropTypes.bool,
    withSelectOption: PropTypes.bool,
    selectLabel: PropTypes.string,
    isViewOnly: PropTypes.bool,
  }

  static defaultProps = {
    country: '',
    value: '',

    onlyCountries: [],
    preferredCountries: [],
    excludeCountries: [],

    placeholder: '(702) 123-4567',
    searchPlaceholder: 'Search',
    searchNotFound: 'No entries to show',
    flagsImagePath: './flags.png',
    disabled: false,

    containerStyle: {},
    inputStyle: {},
    buttonStyle: {},
    dropdownStyle: {},
    searchStyle: {},

    containerClass: '',
    inputClass: '',
    buttonClass: '',
    dropdownClass: '',
    searchClass: '',
    className: '',

    autoFormat: true,
    enableAreaCodes: false,
    enableTerritories: false,
    disableCountryCode: false,
    disableDropdown: false,
    enableLongNumbers: false,
    countryCodeEditable: true,
    enableSearch: false,
    disableSearchIcon: false,
    disableInitialCountryGuess: false,
    disableCountryGuess: false,

    regions: '',

    inputProps: {},
    localization: {},

    masks: null,
    priority: null,
    areaCodes: null,

    preserveOrder: [],

    defaultMask: "....................", // prefix+dialCode+' '+defaultMask
    alwaysDefaultMask: false,
    prefix: '+',
    copyNumbersOnly: true,
    renderStringAsFlag: '',
    autocompleteSearch: false,
    jumpCursorToEnd: true,
    enableAreaCodeStretch: false,
    enableClickOutside: true,
    showDropdown: false,

    isValid: true, // (value, selectedCountry, onlyCountries, hiddenAreaCodes) => true | false | 'Message'
    defaultErrorMessage: '',
    specialLabel: 'Phone',
    control: {},
    clearValue: false,
    withSelectOption: false,
    selectLabel: 'Select',
    onEnterKeyPress: null, // null or function
    isViewOnly: false,
    makeFocus: false,

    keys: {
      UP: 38, DOWN: 40, RIGHT: 39, LEFT: 37, ENTER: 13,
      ESC: 27, PLUS: 43, A: 65, Z: 90, SPACE: 32, TAB: 9,
    }
  }

  constructor(props) {
    super(props);
    const { onlyCountries, preferredCountries, hiddenAreaCodes } = new CountryData(
      props.enableAreaCodes, props.enableTerritories, props.regions,
      props.onlyCountries, props.preferredCountries, props.excludeCountries, props.preserveOrder,
      props.masks, props.priority, props.areaCodes, props.localization,
      props.prefix, props.defaultMask, props.alwaysDefaultMask, props.withSelectOption,
      props.selectLabel,
    );

    const inputNumber = props.value ? props.value.replace(/\D/g, '') : '';

    let countryGuess;
    if (props.disableInitialCountryGuess) {
      countryGuess = 0;
    } else if (inputNumber.length > 1) {
      // Country detect by phone
      countryGuess = this.guessSelectedCountry(inputNumber.substring(0, 6), props.country, onlyCountries, hiddenAreaCodes) || 0;
    } else if (props.country) {
      // Default country
      countryGuess = onlyCountries.find(o => o.iso2 == props.country) || 0;
    } else {
      // Empty params
      countryGuess = 0;
    }

    if (props.withSelectOption) {
      countryGuess = onlyCountries.find(o => o.iso2 == FAKE_SELECT_COUNTRY_ISO) || 0;
    }

    const dialCode = (
      inputNumber.length < 2 &&
      countryGuess &&
      !startsWith(inputNumber, countryGuess.dialCode)
    ) ? countryGuess.dialCode : '';

    // formattedNumber = (inputNumber === '' && countryGuess === 0) ? '' : // Need to check this condition if required
    let formattedNumber = this.formatNumber(
      (props.disableCountryCode ? '' : dialCode) + inputNumber,
      countryGuess.name ? countryGuess : undefined
    );

    const highlightCountryIndex = onlyCountries.findIndex(o => o == countryGuess);

    this.state = {
      showDropdown: props.showDropdown,

      formattedNumber,
      onlyCountries,
      preferredCountries,
      hiddenAreaCodes,
      selectedCountry: countryGuess,
      highlightCountryIndex,

      queryString: '',
      freezeSelection: false,
      debouncedQueryStingSearcher: debounce(this.searchCountry, 250),
      searchValue: '',
    };
  }

  componentDidMount() {
    if (document.addEventListener && this.props.enableClickOutside) {
      document.addEventListener('mousedown', this.handleClickOutside);
    }
    if (this.props.onMount) {
      this.props.onMount(this.state.formattedNumber.replace(/\D+/g, ''), this.getCountryData(), this.state.formattedNumber)
    }
  }

  componentWillUnmount() {
    if (document.removeEventListener && this.props.enableClickOutside) {
      document.removeEventListener('mousedown', this.handleClickOutside);
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.makeFocus !== this.props.makeFocus) {
      this.numberInputRef.focus();
    }
    if (prevProps.clearValue !== this.props.clearValue) {
      this.updateFormattedNumber('');
      if (this.props.withSelectOption) {
        this.setState({
          selectedCountry: this.state.onlyCountries.find(o => o.iso2 == FAKE_SELECT_COUNTRY_ISO) || 0
        })
      }
    }

    if (prevProps.country !== this.props.country) {
      this.updateCountry(this.props.country);
      if (this.props.country === '' && this.props.withSelectOption) {
        this.setState({
          selectedCountry: this.state.onlyCountries.find(o => o.iso2 == FAKE_SELECT_COUNTRY_ISO) || 0
        })
      }
    }

    if (prevProps.value !== this.props.value) {
      this.updateFormattedNumber(this.props.value);
    }
  }

  getProbableCandidate = memoize((queryString) => {
    if (!queryString || queryString.length === 0) {
      return null;
    }
    // don't include the preferred countries in search
    const probableCountries = this.state.onlyCountries.filter((country) => {
      return startsWith(country.name.toLowerCase(), queryString.toLowerCase());
    }, this);
    return probableCountries[0];
  });

  // Helper function to find main country code from hidden area codes
  findMainCountryCode = (inputNumber, onlyCountries, hiddenAreaCodes) => {
    const mainCode = hiddenAreaCodes.find(hc =>
      startsWith(inputNumber, hc.dialCode) &&
      onlyCountries.some(oc => hc.iso2 === oc.iso2 && oc.mainCode)
    );
    if (mainCode) {
      return onlyCountries.find(oc => mainCode.iso2 === oc.iso2 && oc.mainCode) || null;
    }
    return null;
  };

  guessSelectedCountry = memoize((inputNumber, country, onlyCountries, hiddenAreaCodes) => {
    // Try hidden area codes if area codes are disabled
    if (!this.props.enableAreaCodes) {
      return this.findMainCountryCode(inputNumber, onlyCountries, hiddenAreaCodes);
    }

    if (!inputNumber.trim()) {
      return onlyCountries.find(o => o.iso2 == country) || null;
    }

    let bestGuess = null;
    let maxDialCodeLen = 0;
    let minPriority = Infinity;

    for (const c of onlyCountries) {
      if (startsWith(inputNumber, c.dialCode)) {
        if (
          c.dialCode.length > maxDialCodeLen ||
          (c.dialCode.length === maxDialCodeLen && c.priority < minPriority)
        ) {
          bestGuess = c;
          maxDialCodeLen = c.dialCode.length;
          minPriority = c.priority;
        }
      }
    }

    return bestGuess || onlyCountries.find(o => o.iso2 == country) || null;
  });

  // Hooks for updated props
  updateCountry = (country) => {
    const { onlyCountries } = this.state

    let newSelectedCountry;
    if (country.indexOf(0) >= '0' && country.indexOf(0) <= '9') { // digit
      newSelectedCountry = onlyCountries.find(o => o.dialCode == +country);
    } else {
      newSelectedCountry = onlyCountries.find(o => o.iso2 == country);
    }

    if (newSelectedCountry && newSelectedCountry.dialCode) {
      this.setState({
        selectedCountry: newSelectedCountry,
        formattedNumber: this.props.disableCountryCode ? '' : this.formatNumber(newSelectedCountry.dialCode, newSelectedCountry),
      });
    }
  }

  updateFormattedNumber(value) {
    if (value === null) {
      return;
    }
    this.setState({ selectedCountry: 0, formattedNumber: "" });

    const { onlyCountries, selectedCountry, hiddenAreaCodes } = this.state;
    const { country, prefix, disableCountryGuess, disableCountryCode } = this.props;

    if (value === '') {
      return;
    }
    this.setState({ selectedCountry, formattedNumber: "" });

    const inputNumber = value.replace(/\D/g, '');
    let newSelectedCountry = selectedCountry;
    let formattedNumber;

    const startsWithDialCode = selectedCountry && startsWith(value, prefix + selectedCountry.dialCode);

    if (!startsWithDialCode) {
      if (!disableCountryGuess) {
        newSelectedCountry = this.guessSelectedCountry(inputNumber.substring(0, 6), country, onlyCountries, hiddenAreaCodes) || selectedCountry;
      }
    }

    const dialCode = newSelectedCountry && startsWith(inputNumber, prefix + newSelectedCountry.dialCode)
      ? newSelectedCountry.dialCode
      : '';

    formattedNumber = this.formatNumber(
      (disableCountryCode ? '' : dialCode) + inputNumber,
      newSelectedCountry || undefined
    );

    this.setState({ selectedCountry: newSelectedCountry, formattedNumber });
  }

  // View methods
  scrollTo = (country, middle) => {
    if (!country) return;
    const container = this.dropdownRef;
    if (!container || !document.body) return;

    const containerHeight = container.offsetHeight;
    const containerOffset = container.getBoundingClientRect();
    const containerTop = containerOffset.top + document.body.scrollTop;
    const containerBottom = containerTop + containerHeight;

    const element = country;
    const elementOffset = element.getBoundingClientRect();

    const elementHeight = element.offsetHeight;
    const elementTop = elementOffset.top + document.body.scrollTop;
    const elementBottom = elementTop + elementHeight;

    let newScrollTop = elementTop - containerTop + container.scrollTop;
    const middleOffset = (containerHeight / 2) - (elementHeight / 2);

    if (this.props.enableSearch ? elementTop < containerTop + 32 : elementTop < containerTop) {
      // scroll up
      if (middle) {
        newScrollTop -= middleOffset;
      }
      container.scrollTop = newScrollTop;
    }
    else if (elementBottom > containerBottom) {
      // scroll down
      if (middle) {
        newScrollTop += middleOffset;
      }
      const heightDifference = containerHeight - elementHeight;
      container.scrollTop = newScrollTop - heightDifference;
    }
  }

  scrollToTop = () => {
    const container = this.dropdownRef;
    if (!container || !document.body) return;
    container.scrollTop = 0;
  }

  formatWithPattern = (text, format, country) => {
    const { disableCountryCode, enableAreaCodeStretch, enableLongNumbers } = this.props;
    // Get pattern based on props
    let pattern = format;
    if (disableCountryCode) {
      pattern = pattern.split(' ').slice(1).join(' ');
    } else if (enableAreaCodeStretch && country.isAreaCode) {
      const parts = pattern.split(' ');
      parts[1] = parts[1].replace(/\.+/, ''.padEnd(country.areaCodeLength, '.'));
      pattern = parts.join(' ');
    }

    // Format number using pattern
    let formattedText = '';
    let remainingText = text.split('');
    for (const char of pattern) {
      if (remainingText.length === 0) break;
      if (char === '.') {
        formattedText += remainingText.shift();
      } else {
        formattedText += char;
      }
    }
    let formattedNumber = enableLongNumbers
      ? formattedText + remainingText.join('')
      : formattedText;

    // Always close brackets
    if (formattedNumber.includes('(') && !formattedNumber.includes(')')) {
      formattedNumber += ')';
    }
    return formattedNumber;
  }

  formatNumber = (text, country) => {
    if (!country) return text;

    const { format } = country;
    const { disableCountryCode, autoFormat, prefix } = this.props;

    if (!text || text.length === 0) {
      return disableCountryCode ? '' : prefix;
    }

    // For short text or no pattern/autoFormat, just return as is
    if (text.length < 2 || !format || !autoFormat) {
      return disableCountryCode ? text : prefix + text;
    }

    return this.formatWithPattern(text, format, country);
  }

  // Put the cursor to the end of the input (usually after a focus event)
  cursorToEnd = () => {
    const input = this.numberInputRef;
    if (document.activeElement !== input) return;
    input.focus();
    let len = input.value.length;
    if (input.value.charAt(len - 1) === ')') len = len - 1;
    input.setSelectionRange(len, len);
  }

  getElement = (index) => {
    return this[`flag_no_${index}`];
  }

  // return country data from state
  getCountryData = () => {
    if (!this.state.selectedCountry) return {}
    return {
      name: this.state.selectedCountry.name || '',
      dialCode: this.state.selectedCountry.dialCode || '',
      countryCode: this.state.selectedCountry.iso2 || '',
      format: this.state.selectedCountry.format || ''
    }
  }



  handleFlagDropdownClick = (e) => {
    e.preventDefault();
    if (!this.state.showDropdown && this.props.disabled) return;
    const { preferredCountries, onlyCountries, selectedCountry } = this.state
    const allCountries = this.concatPreferredCountries(preferredCountries, onlyCountries);

    const highlightCountryIndex = allCountries.findIndex(o =>
      o.dialCode === selectedCountry.dialCode && o.iso2 === selectedCountry.iso2);

    this.setState({
      showDropdown: !this.state.showDropdown,
      highlightCountryIndex,
    }, () => {
      if (this.state.showDropdown) {
        this.scrollTo(this.getElement(this.state.highlightCountryIndex));
      }
    });
  }

  // Helper: Get main dial code for country
  getMainDialCode = (countryObj, onlyCountries) => {
    if (countryObj.hasAreaCodes) {
      const mainCountry = onlyCountries.find(o => o.iso2 === countryObj.iso2 && o.mainCode);
      return mainCountry ? mainCountry.dialCode : countryObj.dialCode;
    }
    return countryObj.dialCode;
  };


  getNewSelectedCountry = (value, digits) => {
    const { country, disableCountryGuess } = this.props;
    const { onlyCountries, selectedCountry, hiddenAreaCodes, freezeSelection, } = this.state;
    let newSelectedCountry = selectedCountry;
    let shouldFreezeSelection = freezeSelection;
    if (value.length > 0) {
      const inputNumber = digits;
      const shouldGuess = !freezeSelection || (selectedCountry && selectedCountry.dialCode.length > inputNumber.length);
      if (shouldGuess) {
        newSelectedCountry = disableCountryGuess
          ? selectedCountry
          : this.guessSelectedCountry(inputNumber.substring(0, 6), country, onlyCountries, hiddenAreaCodes) || selectedCountry;
        shouldFreezeSelection = false;
      }
      newSelectedCountry = newSelectedCountry.dialCode ? newSelectedCountry : selectedCountry;
    }
    return { newSelectedCountry, shouldFreezeSelection };
  };

  // Update state and caret
  updateInputStateAndCaret = (e, onChange, formattedNumber, shouldFreezeSelection, newSelectedCountry,) => {

    const oldCaretPosition = e.target.selectionStart;
    let caretPosition = e.target.selectionStart;
    const oldFormattedText = this.state.formattedNumber;
    const diff = formattedNumber.length - oldFormattedText.length;

    this.setState({
      formattedNumber,
      freezeSelection: shouldFreezeSelection,
      selectedCountry: newSelectedCountry,
    }, () => {
      if (diff > 0) {
        caretPosition = caretPosition - diff;
      }

      const lastChar = formattedNumber.charAt(formattedNumber.length - 1);
      if (lastChar == ')') {
        this.numberInputRef.setSelectionRange(formattedNumber.length - 1, formattedNumber.length - 1);
      } else if (caretPosition > 0 && oldFormattedText.length >= formattedNumber.length) {
        this.numberInputRef.setSelectionRange(caretPosition, caretPosition);
      } else if (oldCaretPosition < oldFormattedText.length) {
        this.numberInputRef.setSelectionRange(oldCaretPosition, oldCaretPosition);
      }

      if (onChange) onChange(formattedNumber.replace(/\D+/g, ''), this.getCountryData(), e, formattedNumber);
    });
  }

  // Enforce max length for phone number digits
  enforceMaxLength = (digits, enableLongNumbers) => {
    if (digits.length > 15) {
      if (enableLongNumbers === false) return false;
      if (typeof enableLongNumbers === 'number' && digits.length > enableLongNumbers) return false;
    }
    return true;
  }

  handleInput = (e) => {
    const { value } = e.target;
    const { prefix, onChange, disableCountryCode, countryCodeEditable, enableLongNumbers } = this.props;
    const { onlyCountries, selectedCountry, formattedNumber: prevFormattedNumber } = this.state;


    // Prevent editing country code if not editable
    if (!countryCodeEditable) {
      const mainCode = this.getMainDialCode(selectedCountry, onlyCountries);
      const expectedPrefix = prefix + mainCode;
      if (value.slice(0, expectedPrefix.length) !== expectedPrefix) return;
    }

    // Handle empty input (just prefix)
    if (value === prefix) {
      if (onChange) onChange('', this.getCountryData(), e, '');
      this.setState({ formattedNumber: '' });
      return;
    }

    // Enforce max length
    const digits = value.replace(/\D/g, '');
    if (!this.enforceMaxLength(digits, enableLongNumbers)) return;

    // Ignore unchanged input
    if (value === prevFormattedNumber) return;

    // Prevent default event
    if (e.preventDefault) e.preventDefault(); else e.returnValue = false;
    if (onChange) e.persist();

    // Guess country if needed
    const { newSelectedCountry, shouldFreezeSelection } = this.getNewSelectedCountry(value, digits);

    // Format number
    const defaultFormattedNumber = disableCountryCode ? '' : prefix;
    const formattedNumber = value.length > 0
      ? this.formatNumber(digits, newSelectedCountry)
      : defaultFormattedNumber;

    this.updateInputStateAndCaret(e, onChange, formattedNumber, shouldFreezeSelection, newSelectedCountry);
  }

  handleInputClick = (e) => {
    this.setState({ showDropdown: false });
    if (this.props.onClick) this.props.onClick(e, this.getCountryData());
  }

  handleDoubleClick = (e) => {
    const len = e.target.value.length;
    e.target.setSelectionRange(0, len);
  }

  handleFlagItemClick = (country, e) => {
    const newSelectedCountry = this.state.onlyCountries.find(o => o == country);
    if (!newSelectedCountry) return;

    const unformattedNumber = this.state.formattedNumber.replace(' ', '').replace('(', '').replace(')', '').replace('-', '');
    const formattedNumber = this.formatNumber(unformattedNumber.replace(/\D/g, ''), newSelectedCountry);

    this.setState({
      showDropdown: false,
      selectedCountry: newSelectedCountry,
      freezeSelection: true,
      formattedNumber,
      searchValue: ''
    }, () => {
      this.cursorToEnd();
      if (this.props.onChange) this.props.onChange(formattedNumber.replace(/\D+/g, ''), this.getCountryData(), e, formattedNumber);
    });
  }

  handleInputFocus = (e) => {
    // if the input is blank, insert dial code of the selected country
    if (this.numberInputRef) {
      if (this.numberInputRef.value === this.props.prefix && this.state.selectedCountry && !this.props.disableCountryCode) {
        this.setState({
          formattedNumber: this.props.prefix + this.state.selectedCountry.dialCode
        }, () => { this.props.jumpCursorToEnd && setTimeout(this.cursorToEnd, 0) });
      }
    }

    this.setState({ placeholder: '' });

    this.props.onFocus && this.props.onFocus(e, this.getCountryData());
    this.props.jumpCursorToEnd && setTimeout(this.cursorToEnd, 0);
  }

  handleInputBlur = (e) => {
    if (!e.target.value) this.setState({ placeholder: this.props.placeholder });
    this.props.onBlur && this.props.onBlur(e, this.getCountryData());
  }

  handleInputCopy = (e) => {
    if (!this.props.copyNumbersOnly) return;
    const text = window.getSelection().toString().replace(/\D+/g, '');
    e.clipboardData.setData('text/plain', text);
    e.preventDefault();
  }

  getHighlightCountryIndex = (direction) => {
    // had to write own function because underscore does not have findIndex. lodash has it
    const highlightCountryIndex = this.state.highlightCountryIndex + direction;

    if (highlightCountryIndex < 0 || highlightCountryIndex >= (this.state.onlyCountries.length + this.state.preferredCountries.length)) {
      return highlightCountryIndex - direction;
    }

    if (this.props.enableSearch && highlightCountryIndex > this.getSearchFilteredCountries().length) return 0; // select first country
    return highlightCountryIndex;
  }

  searchCountry = () => {
    const probableCandidate = this.getProbableCandidate(this.state.queryString) || this.state.onlyCountries[0];
    const probableCandidateIndex = this.state.onlyCountries.findIndex(o => o == probableCandidate) + this.state.preferredCountries.length;

    this.scrollTo(this.getElement(probableCandidateIndex), true);

    this.setState({ queryString: '', highlightCountryIndex: probableCandidateIndex });
  }

  handleDropdownKeyNavigation = (e) => {
    const { keys } = this.props;

    const moveHighlight = direction => {
      this.setState(
        { highlightCountryIndex: this.getHighlightCountryIndex(direction) },
        () => this.scrollTo(this.getElement(this.state.highlightCountryIndex), true)
      );
    };

    switch (e.which) {
      case keys.DOWN:
        moveHighlight(1);
        break;
      case keys.UP:
        moveHighlight(-1);
        break;
      case keys.ENTER: {
        const countries = this.props.enableSearch
          ? this.getSearchFilteredCountries()
          : [...this.state.preferredCountries, ...this.state.onlyCountries];
        const country = countries[this.state.highlightCountryIndex] || countries[0];
        this.handleFlagItemClick(country, e);
        break;
      }
      case keys.ESC:
      case keys.TAB:
        this.setState({ showDropdown: false }, this.cursorToEnd);
        break;
      default:
        if ((e.which >= keys.A && e.which <= keys.Z) || e.which === keys.SPACE) {
          this.setState(
            { queryString: this.state.queryString + String.fromCharCode(e.which) },
            this.state.debouncedQueryStingSearcher
          );
        }
    }
  }

  handleKeydown = (e) => {
    const { keys } = this.props;
    const { className } = e.target;

    // Handle flag dropdown open
    if (className.includes('selected-flag') && e.which === keys.ENTER && !this.state.showDropdown) {
      this.handleFlagDropdownClick(e);
      return;
    }

    // Handle input blur on Enter/Esc
    if (className.includes('form-control') && (e.which === keys.ENTER || e.which === keys.ESC)) {
      e.target.blur();
      return;
    }

    // Ignore if dropdown is closed or disabled
    if (!this.state.showDropdown || this.props.disabled) return;

    // Handle search box navigation
    if (className.includes('search-box')) {
      if (![keys.UP, keys.DOWN, keys.ENTER].includes(e.which)) {
        if (e.which !== keys.ESC || e.target.value !== '') return;
      }
    }

    e.preventDefault?.();

    this.handleDropdownKeyNavigation(e)
  }

  handleInputKeyDown = (e) => {
    const { keys, onEnterKeyPress, onKeyDown } = this.props;
    if (e.which === keys.ENTER) {
      if (onEnterKeyPress) onEnterKeyPress(e);
    }
    if (onKeyDown) onKeyDown(e);
  }

  handleClickOutside = (e) => {
    if (this.dropdownRef && !this.dropdownContainerRef.contains(e.target)) {
      this.state.showDropdown && this.setState({ showDropdown: false });
    }
  }

  handleSearchChange = (event) => {
    const { currentTarget: { value: searchValue } } = event;
    const { preferredCountries, selectedCountry } = this.state
    let highlightCountryIndex = 0;

    if (searchValue === '' && selectedCountry) {
      const { onlyCountries } = this.state
      highlightCountryIndex = this.concatPreferredCountries(preferredCountries, onlyCountries).findIndex(o => o == selectedCountry);
      // wait asynchronous search results re-render, then scroll
      setTimeout(() => this.scrollTo(this.getElement(highlightCountryIndex)), 100)
    }
    this.setState({ searchValue, highlightCountryIndex });
  }

  concatPreferredCountries = (preferredCountries, onlyCountries) => {
    if (preferredCountries.length > 0) { return [...new Set(preferredCountries.concat(onlyCountries))] }
    else { return onlyCountries }
  }

  getDropdownCountryName = (country) => {
    return country.localName || country.name;
  }

  getSearchFilteredCountries = () => {
    const { preferredCountries, onlyCountries, searchValue } = this.state
    const { enableSearch } = this.props
    const allCountries = this.concatPreferredCountries(preferredCountries, onlyCountries);
    const sanitizedSearchValue = searchValue.trim().toLowerCase().replace('+', '');
    if (enableSearch && sanitizedSearchValue) {
      // [...new Set()] to get rid of duplicates
      // firstly search by iso2 code
      if (/^\d+$/.test(sanitizedSearchValue)) { // contains digits only
        // values wrapped in ${} to prevent undefined
        return allCountries.filter(({ dialCode }) =>
          [`${dialCode}`].some(field => field.toLowerCase().includes(sanitizedSearchValue)))
      } else {
        const iso2countries = allCountries.filter(({ iso2 }) =>
          [`${iso2}`].some(field => field.toLowerCase().includes(sanitizedSearchValue) && iso2.toLowerCase() !== FAKE_SELECT_COUNTRY_ISO.toLowerCase()))
        // || '' - is a fix to prevent search of 'undefined' strings
        // Since all the other values shouldn't be undefined, this fix was accepte
        // but the structure do not looks very good
        const searchedCountries = allCountries.filter(({ name, localName, iso2 }) =>
          [`${name}`, `${localName || ''}`].some(field => field.toLowerCase().includes(sanitizedSearchValue) && iso2.toLowerCase() !== FAKE_SELECT_COUNTRY_ISO.toLowerCase()))
        this.scrollToTop()
        return [...new Set([].concat(iso2countries, searchedCountries))]
      }
    } else {
      return allCountries
    }
  }
  // Determine classes and dial code for each country item
  isPreferred = (country) => { return country.iso2 === 'us' || country.iso2 === 'uk' };

  getCountryDropdownList = () => {
    const { preferredCountries, highlightCountryIndex, showDropdown, searchValue } = this.state;
    const { disableDropdown, prefix } = this.props
    const { enableSearch, searchNotFound, searchClass, searchStyle, searchPlaceholder, autocompleteSearch } = this.props;

    const searchedCountries = this.getSearchFilteredCountries()

    let countryDropdownList = searchedCountries.map((country, index) => {
      const highlight = highlightCountryIndex === index;


      const itemClasses = classNames({
        country: true,
        preferred: this.isPreferred(country),
        active: country.iso2 === 'us',
        highlight,
      });

      const inputFlagClasses = `flag ${country.iso2}`;
      let dialCode = prefix + country.dialCode;

      // Format dial code if country has a format
      if (country.format) {
        dialCode = this.formatNumber(prefix + country.dialCode, country).replace(/[() -]/g, "");
      }

      return (
        <li
          ref={(el) => (this[`flag_no_${index}`] = el)}
          key={`flag_${country.iso2}_${country.dialCode}`}
          data-flag-key={`flag_no_${index}`}
          className={itemClasses}
          data-dial-code="1"
          tabIndex={disableDropdown ? "-1" : "0"}
          data-country-code={country.iso2}
          onClick={(e) => this.handleFlagItemClick(country, e)}
          role="option"
          {...(highlight ? { "aria-selected": true } : {})}
        >

          <div className={inputFlagClasses} />
          <span className="country-name">
            {this.getDropdownCountryName(country)}
          </span>
          <span className="dial-code">{dialCode}</span>
        </li>
      );
    });

    // Insert a dashed divider between preferred countries and others
    const shouldShowDivider =
      preferredCountries.length > 0 &&
      (!enableSearch || (enableSearch && !searchValue.trim()));
    if (shouldShowDivider) {
      countryDropdownList.splice(
        preferredCountries.length,
        0,
        <li key="dashes" className="divider" />
      );
    }

    const dropDownClasses = classNames(
      "country-list",
      { hide: !showDropdown },
      this.props.dropdownClass
    );

    return (
      <ul
        ref={(el) => {
          if (!enableSearch && el) el.focus();
          this.dropdownRef = el;
        }}
        className={dropDownClasses}
        style={this.props.dropdownStyle}
        role="listbox"
        tabIndex="0"
      >
        {enableSearch && (
          <li className={classNames("search", { [searchClass]: !!searchClass })}>
            <div className="search-container">
              <Search className="search-icon" />
              <input
                className="search-box"
                style={searchStyle}
                type="search"
                placeholder={searchPlaceholder}
                autoFocus
                autoComplete={autocompleteSearch ? "on" : "off"}
                value={searchValue}
                onChange={this.handleSearchChange}
              />
            </div>
          </li>
        )}
        {countryDropdownList.length > 0 ? (
          countryDropdownList
        ) : (
          <li className="no-entries-message">
            <span>{searchNotFound}</span>
          </li>
        )}
      </ul>
    );
  }

  validateInput = () => {
    const { isValid, defaultErrorMessage } = this.props;
    const { formattedNumber, selectedCountry, onlyCountries, hiddenAreaCodes } = this.state;
    let isValidValue = true, errorMessage = '';
    if (typeof isValid === 'boolean') {
      isValidValue = isValid;
    } else if (typeof isValid === 'function') {
      const result = isValid(formattedNumber.replace(/\D/g, ''), selectedCountry, onlyCountries, hiddenAreaCodes);
      if (typeof result === 'boolean') {
        isValidValue = result;
        if (!isValidValue) errorMessage = defaultErrorMessage;
      } else if (typeof result === 'string') {
        isValidValue = false;
        errorMessage = result;
      }
    }
    return { isValidValue, errorMessage };
  }

  getSelectedCountryTitle = () => {
    const { selectedCountry } = this.state;
    return selectedCountry
      ? `${selectedCountry.localName || selectedCountry.name}: + ${selectedCountry.dialCode}`
      : "";
  }

  handleInputRef = (el) => {
    this.numberInputRef = el;
    const { inputProps } = this.props;
    if (!inputProps.ref) return;
    
    if (typeof inputProps.ref === "function") {
      inputProps.ref(el);
      return;
    }
    
    if (typeof inputProps.ref === "object") {
      inputProps.ref.current = el;
    }
  }

  renderFlagContent = () => {
    const { selectedCountry } = this.state;
    const { prefix, selectLabel } = this.props;
    const countryCode = 'country-code';
    const inputFlagClasses = `flag ${selectedCountry && selectedCountry.iso2}`;
    
    if (selectedCountry.iso2 === FAKE_SELECT_COUNTRY_ISO) {
      return <label className="country-label-select">{selectLabel}</label>;
    }
    
    return (
      <>
        <div className={countryCode}>
          <label className="country-label">
            {selectedCountry?.iso2?.toUpperCase()}
          </label>
          <label className="country-label-dial-code">
            {prefix}
            {selectedCountry.dialCode}
          </label>
        </div>
        <div className={inputFlagClasses}></div>
      </>
    );
  }

  getFlagDropdownClick = () => {
    const { disableDropdown } = this.props;
    return disableDropdown ? undefined : this.handleFlagDropdownClick;
  }

  getContainerStyle = () => {
    return this.props.style || this.props.containerStyle;
  }

  render() {
    const { selectedCountry, showDropdown, formattedNumber } = this.state;
    const { disableDropdown, renderStringAsFlag, specialLabel, isViewOnly } = this.props;

    if (isViewOnly) {
      return (
        <span className="phone-text-view" style={this.props.inputStyle}>
          {formattedNumber}
        </span>
      );
    }

    const { isValidValue, errorMessage } = this.validateInput();
    const containerClasses = classNames({
      [this.props.containerClass]: true,
      'react-tel-input': true,
    });
    const arrowClasses = classNames({ 'arrow': true, 'up': showDropdown });
    const inputClasses = classNames({
      'form-control': true,
      'invalid-number': !isValidValue,
      'open': showDropdown,
      [this.props.inputClass]: true,
    });
    const selectedFlagClasses = classNames({
      'selected-flag': true,
      'open': showDropdown,
    });
    const flagViewClasses = classNames({
      'flag-dropdown': true,
      'invalid-number': !isValidValue,
      'open': showDropdown,
      [this.props.buttonClass]: true,
    });

    return (
      <div
        className={`${containerClasses} ${this.props.className}`}
        style={this.getContainerStyle()}
        onKeyDown={this.handleKeydown}
      >
        {specialLabel && <div className="special-label">{specialLabel}</div>}
        {errorMessage && (
          <div className="invalid-number-message">{errorMessage}</div>
        )}
        <input
          className={inputClasses}
          style={this.props.inputStyle}
          onChange={this.handleInput}
          onClick={this.handleInputClick}
          onDoubleClick={this.handleDoubleClick}
          onFocus={this.handleInputFocus}
          onBlur={this.handleInputBlur}
          onCopy={this.handleInputCopy}
          value={formattedNumber}
          onKeyDown={this.handleInputKeyDown}
          placeholder={this.props.placeholder}
          disabled={this.props.disabled}
          type="tel"
          {...this.props.inputProps}
          ref={this.handleInputRef}
        />

        <div
          className={flagViewClasses}
          style={this.props.buttonStyle}
          ref={(el) => (this.dropdownContainerRef = el)}
        >
          {renderStringAsFlag ? (
            <div className={selectedFlagClasses}>{renderStringAsFlag}</div>
          ) : (
            <div
              onClick={this.getFlagDropdownClick()}
              className={selectedFlagClasses}
              title={this.getSelectedCountryTitle()}
              tabIndex={disableDropdown ? "-1" : "0"}
              role="button"
              aria-haspopup="listbox"
              aria-expanded={showDropdown || undefined}
            >
              {!disableDropdown && (
                <div className={arrowClasses}>
                  <ExpandIcon />
                </div>
              )}
              {this.renderFlagContent()}
            </div>
          )}

          {showDropdown && this.getCountryDropdownList()}
        </div>
      </div>
    );
  }
}

export default PhoneInput;
